import { InputNumber } from "@/components/InputNumber";
import { selectStandardProps } from "@/utils/componentUtils";
import { resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { Checkbox, Col, Form, Modal, Row, Select } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useCreateCoverageLimit, useUpdateCoverageLimit } from "../../../../common/api/queries/coverageLimit";
import HiddenInput from "../../../../common/components/form/components/HiddenInput";
import { ModalSizes, rowGutter } from "../../../../common/constants";
import InstitutionSelect from "../../../enumerations/components/form/InstitutionSelect";
import { InstitutionType } from "../../../institution/enums";
import { CoverageLimitEnum } from "../../enums";
import { CoverageLimit, CreateUpdateCoverageLimit } from "../../types";

interface Props {
  coverageLimit?: CoverageLimit;
  open: boolean;
  onFormCancel: () => void;
}

export const CoverageLimitForm = ({ coverageLimit, open, onFormCancel }: Props) => {
  const { t } = useTranslation();

  const createCoverageLimit = useCreateCoverageLimit();
  const updateCoverageLimit = useUpdateCoverageLimit();

  const [form] = Form.useForm<CreateUpdateCoverageLimit>();

  useEffect(() => {
    if (open && coverageLimit) {
      form.setFieldsValue({ ...coverageLimit, institutionId: coverageLimit.institution.id });
    }
  }, [coverageLimit, open, form]);

  const handleFormSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        if (coverageLimit) {
          updateCoverageLimit.mutate(
            { id: coverageLimit.id, object: values },
            {
              onSuccess: onFormCancel,
              onError: (error) => setErrorsToForm(form, "coverageLimit.attrs", error.violations),
            },
          );
        } else {
          createCoverageLimit.mutate(values, {
            onSuccess: onFormCancel,
            onError: (error) => setErrorsToForm(form, "coverageLimit.attrs", error.violations),
          });
        }
      })
      .catch(resolveFormValidationError);
  };

  return (
    <Modal
      width={ModalSizes.MEDIUM}
      open={open}
      title={coverageLimit ? t("coverageLimit.titles.updateLimit") : t("coverageLimit.titles.createLimit")}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={createCoverageLimit.isPending || updateCoverageLimit.isPending}
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      mask={{ closable: false }}
    >
      <Form form={form} layout="vertical" name="coverageLimitForm">
        <HiddenInput name="optimisticLockVersion" />
        <Row gutter={rowGutter}>
          <Col span={12}>
            <InstitutionSelect
              formItemProps={{
                name: "institutionId",
                label: t("coverageLimit.attrs.institutionId"),
                rules: [validations.notNull],
              }}
              optionsProps={{
                includeDeactivated: true,
                filterType: InstitutionType.INSURANCE_COMPANY,
              }}
            />
          </Col>

          <Col span={12}>
            <Form.Item
              name="coverageLimitEnum"
              label={t("coverageLimit.enums.coverageLimitEnum._label")}
              rules={[validations.none]}
            >
              <Select
                {...selectStandardProps}
                allowClear
                options={Object.keys(CoverageLimitEnum).map((limit) => ({
                  value: limit,
                  label: t("coverageLimit.enums.coverageLimitEnum." + limit),
                }))}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={rowGutter}>
          <Col span={8}>
            <Form.Item
              name="health"
              label={t("coverageLimit.attrs.health")}
              rules={[validations.notNull, validations.minNumber(0)]}
            >
              <InputNumber type="decimal" addon="million" />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name="property"
              label={t("coverageLimit.attrs.property")}
              rules={[validations.notNull, validations.minNumber(0)]}
            >
              <InputNumber type="decimal" addon="million" />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name="deactivated"
              valuePropName="checked"
              rules={[validations.none]}
              className="form-item-without-label"
              initialValue={false}
            >
              <Checkbox>{t("coverageLimit.attrs.deactivated")}</Checkbox>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};
